/**
 * @fileoverview Staff Notification Manager component for managing employee notification preferences.
 * Allows admins/managers to select staff members and configure their notification settings
 * across email, SMS, and internal channels.
 */

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faEnvelope, 
  faMobileAlt, 
  faDesktop,
  faSave, 
  faSpinner, 
  faUser,
  faCheck,
  faTimes,
  faRedo,
  faClock,
  faExclamationTriangle,
  faPaperPlane,
  faChevronDown,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import './StaffNotificationManager.css';

/**
 * Staff Notification Manager Component
 * 
 * Features:
 * - Employee selection with searchable dropdown (for admins)
 * - Multi-channel notification management (email, SMS, internal)
 * - Notification type preferences
 * - Quiet hours configuration
 * - Test notification functionality
 * - Reset to defaults
 * 
 * @returns {React.ReactElement} The staff notification manager interface
 */
const StaffNotificationManager = () => {
  const { user } = useAuth();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [isDefault, setIsDefault] = useState(true);
  const [testingChannel, setTestingChannel] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserEmployee, setCurrentUserEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // External notification types from automation_tasks.py (excluding daily_task_digest which is in Role Manager)
  const notificationTypes = [
    { key: 'weekly_task_summary', label: 'Weekly Task Summary', description: 'Weekly employee task summary every Sunday' },
    { key: 'daily_success_summary', label: 'Daily Success Summary', description: 'Daily automation success summary' },
    { key: 'payment_notifications', label: 'Payment Statement Notifications', description: 'Monthly payment statement emails' },
    { key: 'daily_activity_report', label: 'Daily Activity Report', description: 'Daily activity report at 18:00 UTC' },
    { key: 'weekly_client_reports', label: 'Weekly Client Reports', description: 'Weekly performance reports for clients' },
    { key: 'creative_ideas_alerts', label: 'Creative Ideas Alerts', description: 'Alerts for new creative ideas every 30 minutes' },
    { key: 'automation_failures', label: 'Automation Failure Alerts', description: 'Immediate alerts when automation tasks fail' },
  ];

  // Check user's Cognito groups on mount
  useEffect(() => {
    const checkUserGroups = async () => {
      try {
        const session = await fetchAuthSession();
        const groups = session?.tokens?.idToken?.payload?.['cognito:groups'] || [];
        console.log('[StaffNotificationManager] Cognito groups:', groups);
        
        // Check for ADMIN or SUPER_ADMIN
        const isSuperAdmin = groups.includes('ADMIN') || groups.includes('SUPER_ADMIN');
        console.log('[StaffNotificationManager] Is super admin:', isSuperAdmin);
        setIsAdmin(isSuperAdmin);

        // Fetch employees list for admin
        if (isSuperAdmin) {
          console.log('[StaffNotificationManager] User is super admin - loading employees');
          setLoadingEmployees(true);
          const { tokens } = await fetchAuthSession();
          const token = tokens.idToken.toString();
          
          const response = await axios.get(
            `${API_ENDPOINTS.API_BASE_URL}/api/employees/?active_only=true`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (response.data) {
            setEmployees(response.data);
          }
          setLoadingEmployees(false);
        } else if (user?.userId) {
          // If not super admin, fetch current user's employee data
          console.log('[StaffNotificationManager] Fetching employee data for non-admin:', user.userId);
          const { tokens } = await fetchAuthSession();
          const token = tokens.idToken.toString();
          
          const response = await axios.get(
            `${API_ENDPOINTS.API_BASE_URL}/api/employees/?active_only=true`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (response.data) {
            const currentEmployee = response.data.find(emp => 
              emp.user_id === user.userId || 
              emp.email === user.username
            );
            
            if (currentEmployee) {
              console.log('[StaffNotificationManager] Found current employee:', currentEmployee.first_name, currentEmployee.last_name);
              setCurrentUserEmployee(currentEmployee);
              setSelectedEmployee(currentEmployee);
            }
          }
        }
      } catch (error) {
        console.error('Error checking user groups:', error);
        setLoadingEmployees(false);
      }
    };

    checkUserGroups();
  }, [user]);

  useEffect(() => {
    if (selectedEmployee) {
      fetchPreferences();
    } else {
      setPreferences(null);
      setIsDefault(true);
    }
  }, [selectedEmployee]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter employees based on search term
  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchLower) ||
      emp.user_id.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower)
    );
  });

  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const fetchPreferences = async () => {
    if (!selectedEmployee) return;

    setLoading(true);
    setMessage(null);

    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.get(
        `${API_ENDPOINTS.API_BASE_URL}/api/staff-notifications/preferences/${selectedEmployee.user_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        setPreferences(response.data.preferences);
        setIsDefault(response.data.is_default);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to load notification preferences: ' + (error.response?.data?.detail || error.message)
      });
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!selectedEmployee || !preferences) return;

    setSaving(true);
    setMessage(null);

    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.put(
        `${API_ENDPOINTS.API_BASE_URL}/api/staff-notifications/preferences/${selectedEmployee.user_id}`,
        preferences,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status === 'success') {
        setMessage({ type: 'success', text: 'Notification preferences saved successfully!' });
        setIsDefault(false);
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to save preferences: ' + (error.response?.data?.detail || error.message)
      });
    } finally {
      setSaving(false);
    }
  };

  const resetPreferences = async () => {
    if (!selectedEmployee) return;
    
    if (!window.confirm('Are you sure you want to reset notification preferences to defaults?')) {
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.delete(
        `${API_ENDPOINTS.API_BASE_URL}/api/staff-notifications/preferences/${selectedEmployee.user_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        setMessage({ type: 'success', text: 'Preferences reset to defaults!' });
        setTimeout(() => {
          setMessage(null);
          fetchPreferences();
        }, 1500);
      }
    } catch (error) {
      console.error('Error resetting preferences:', error);
      setMessage({ type: 'error', text: 'Failed to reset preferences' });
    } finally {
      setSaving(false);
    }
  };

  const testNotification = async (channel) => {
    if (!selectedEmployee) return;

    setTestingChannel(channel);
    setMessage(null);

    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.post(
        `${API_ENDPOINTS.API_BASE_URL}/api/staff-notifications/test-notification/${selectedEmployee.user_id}?channel=${channel}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success' || response.data.status === 'warning') {
        setMessage({ 
          type: response.data.status === 'success' ? 'success' : 'warning', 
          text: response.data.message 
        });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      setMessage({ type: 'error', text: 'Failed to send test notification' });
    } finally {
      setTestingChannel(null);
    }
  };

  const updateChannelEnabled = (channel, enabled) => {
    setPreferences(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        enabled
      }
    }));
  };

  const updateNotificationType = (typeKey, enabled) => {
    setPreferences(prev => ({
      ...prev,
      notification_types: {
        ...prev.notification_types,
        [typeKey]: enabled
      }
    }));
  };

  const updateQuietHours = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      quiet_hours: {
        ...prev.quiet_hours,
        [field]: value
      }
    }));
  };

  const updateGeneralSetting = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="lead-tracker-view">
      <div className="staff-notification-manager">
        <div className="manager-header">
          <div>
            <FontAwesomeIcon icon={faBell} className="header-icon" />
            <h1>External Notification Setup</h1>
          </div>
          
          {/* Searchable employee selector for admins */}
          {isAdmin && (
            <div className="inline-employee-selector" ref={dropdownRef}>
              <label>
                <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px' }} />
                Employee:
              </label>
              <div className="custom-dropdown">
                <div 
                  className="dropdown-trigger"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span className={selectedEmployee ? '' : 'placeholder'}>
                    {selectedEmployee 
                      ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
                      : 'Select an employee...'
                    }
                  </span>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                  />
                </div>
                
                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <div className="dropdown-search">
                      <FontAwesomeIcon icon={faSearch} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="dropdown-list">
                      {loadingEmployees ? (
                        <div className="dropdown-loading">
                          <FontAwesomeIcon icon={faSpinner} spin />
                          Loading...
                        </div>
                      ) : filteredEmployees.length === 0 ? (
                        <div className="dropdown-empty">
                          No employees found
                        </div>
                      ) : (
                        filteredEmployees.map((emp) => (
                          <div
                            key={emp.user_id}
                            className={`dropdown-item ${selectedEmployee?.user_id === emp.user_id ? 'selected' : ''}`}
                            onClick={() => handleEmployeeSelect(emp)}
                          >
                            <div className="employee-name">
                              {emp.first_name} {emp.last_name}
                            </div>
                            <div className="employee-meta">
                              {emp.user_id} • {emp.email}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {message && (
          <div className={`notification-message ${message.type}`}>
            <FontAwesomeIcon 
              icon={message.type === 'success' ? faCheck : message.type === 'warning' ? faExclamationTriangle : faTimes} 
            />
            <span>{message.text}</span>
          </div>
        )}

        {/* Show placeholder for non-admins when loading */}
        {!isAdmin && !selectedEmployee && !loading && (
          <div className="empty-state">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="empty-icon" />
            <p>Loading your notification preferences...</p>
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Loading notification preferences...</p>
          </div>
        )}

        {selectedEmployee && isDefault && !loading && (
          <div className="default-notice">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>Using default notification settings. Make changes and save to customize.</span>
          </div>
        )}

        {selectedEmployee && preferences && !loading && (
        <div className="preferences-container">
          {/* External Notification Types */}
          <div className="preference-section">
            <h2>External Notifications</h2>
            <p className="section-description">
              Control which automated email notifications this staff member receives
            </p>

            <div className="notification-types-grid">
              {notificationTypes.map((type) => (
                <div key={type.key} className="notification-type-card">
                  <div className="type-header">
                    <div className="type-info">
                      <h4>{type.label}</h4>
                      <p>{type.description}</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={preferences.notification_types?.[type.key] ?? true}
                        onChange={(e) => updateNotificationType(type.key, e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quiet Hours */}
          <div className="preference-section">
            <h2>
              <FontAwesomeIcon icon={faClock} /> Quiet Hours
            </h2>
            <p className="section-description">
              Set quiet hours when notifications should be suppressed
            </p>

            <div className="quiet-hours-settings">
              <div className="setting-item">
                <div className="setting-info">
                  <label>Enable Quiet Hours</label>
                  <p>Suppress notifications during specified hours</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.quiet_hours?.enabled ?? false}
                    onChange={(e) => updateQuietHours('enabled', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {preferences.quiet_hours?.enabled && (
                <div className="quiet-hours-time-settings">
                  <div className="time-input-group">
                    <label>Start Time</label>
                    <input
                      type="time"
                      value={preferences.quiet_hours?.start_time || '22:00'}
                      onChange={(e) => updateQuietHours('start_time', e.target.value)}
                    />
                  </div>
                  <div className="time-input-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={preferences.quiet_hours?.end_time || '08:00'}
                      onChange={(e) => updateQuietHours('end_time', e.target.value)}
                    />
                  </div>
                  <div className="time-input-group">
                    <label>Timezone</label>
                    <select
                      value={preferences.quiet_hours?.timezone || 'America/New_York'}
                      onChange={(e) => updateQuietHours('timezone', e.target.value)}
                    >
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {selectedEmployee && preferences && (
        <div className="manager-footer">
          <button
            className="reset-btn"
            onClick={resetPreferences}
            disabled={saving || isDefault}
          >
            <FontAwesomeIcon icon={faRedo} />
            <span>Reset to Defaults</span>
          </button>
          <button
            className="save-btn"
            onClick={savePreferences}
            disabled={saving}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span>Save Preferences</span>
              </>
            )}
          </button>
        </div>
      )}

      {!selectedEmployee && (
        <div className="no-employee-selected">
          <FontAwesomeIcon icon={faUser} />
          <h3>Select an Employee</h3>
          <p>Choose an employee from the dropdown above to manage their notification preferences</p>
        </div>
      )}
    </div>
  </div>
  );
};

export default StaffNotificationManager;

